/**
 * Shared Mux Player tuning for Chrome, Safari, Instagram/Meta in-app, and Chromium edge cases.
 * See: https://github.com/muxinc/elements/blob/main/packages/mux-player/REFERENCE.md
 */

import { isInstagramBrowser } from "@/lib/instagramBrowserDetection";

export type MuxPlayerCrossBrowserInput = {
  isInstagram: boolean;
  isArc: boolean;
  isMobile: boolean;
  /** Safari desktop or iOS WebKit (incl. Chrome iOS). */
  isSafariOrIOSWebKit?: boolean;
};

export type PlaybackBrowserEnv = {
  isInstagram: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  /** True Safari (not Chromium). Includes iOS Safari + macOS Safari. */
  isSafari: boolean;
  /** Chrome / Chromium (desktop or Android). False on iOS Chrome (WebKit). */
  isChrome: boolean;
  /** Coarse mobile: narrow viewport OR phone UA. */
  isMobile: boolean;
  /**
   * Autoplay with sound is blocked → start muted (IG IAB, iOS Safari/Chrome, Android mobile).
   */
  needsMutedAutoplay: boolean;
  /**
   * Soft keep-alive after first frame (visibility / stall / canplay).
   * Safe on DualCTA without conversionReel thrash.
   */
  needsSoftKeepAlive: boolean;
  /**
   * Prefer native HLS engine (iOS WebKit + Instagram). MSE is flaky in those WebViews.
   */
  preferNativePlayback: boolean;
};

/**
 * Sync detection — safe for useState initializers (first MuxPlayer paint).
 * Delayed useEffect-only detection was missing webkit-playsinline on Safari first render.
 */
export function detectPlaybackBrowserEnv(): PlaybackBrowserEnv {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      isInstagram: false,
      isIOS: false,
      isAndroid: false,
      isSafari: false,
      isChrome: false,
      isMobile: false,
      needsMutedAutoplay: true,
      needsSoftKeepAlive: true,
      preferNativePlayback: false,
    };
  }

  const ua = navigator.userAgent || "";
  const uaLower = ua.toLowerCase();
  const isInstagram = isInstagramBrowser();
  const isIOS =
    /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /android/i.test(ua);
  // Chrome iOS is CriOS — still WebKit, not Blink.
  const isCriOS = /CriOS/i.test(ua);
  const isFxiOS = /FxiOS/i.test(ua);
  const isSafari =
    isIOS ||
    (/Safari/i.test(ua) &&
      !/Chrome|CriOS|Chromium|Edg|OPR|Firefox|FxiOS|Android/i.test(ua));
  const isChrome =
    !isIOS &&
    !isCriOS &&
    ((/\bChrome\/\d/.test(ua) && !/\bEdg\/\d/.test(ua)) ||
      /Chromium/i.test(ua));
  const isMobileUa =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(
      uaLower,
    );
  const isMobile = isMobileUa || window.innerWidth < 768 || isIOS;

  // Sound-first autoplay fails on: IG/Meta IAB, all iOS WebKit, mobile Chrome/Android.
  const needsMutedAutoplay = isInstagram || isIOS || isCriOS || isFxiOS || isMobile;
  // Soft resume for WebViews + mobile that pause on toolbar / app switch.
  const needsSoftKeepAlive = isInstagram || isIOS || isCriOS || isMobile || isAndroid;
  // Native HLS on WebKit / IG; leave desktop Chrome on Mux default (MSE when needed).
  const preferNativePlayback = isInstagram || isIOS || isCriOS;

  return {
    isInstagram,
    isIOS,
    isAndroid,
    isSafari,
    isChrome,
    isMobile,
    needsMutedAutoplay,
    needsSoftKeepAlive,
    preferNativePlayback,
  };
}

/**
 * Props that improve playback across WebViews and Chromium variants while leaving
 * ordinary desktop browsers on Mux defaults (no forced engine).
 * - Instagram / iOS WebKit: native playback, 720p ladder + maxAutoResolution, strict rendition cap.
 * - Arc / fragile Chromium: HLS.js (MSE) + 720p + rendition cap.
 * - Other mobile: 720p + cap rendition to viewport.
 */
export function getMuxPlayerCrossBrowserProps({
  isInstagram,
  isArc,
  isMobile,
  isSafariOrIOSWebKit = false,
}: MuxPlayerCrossBrowserInput): {
  maxResolution: "720p" | "1080p";
  preferPlayback?: "native" | "mse";
  capRenditionToPlayerSize?: true;
  maxAutoResolution?: "720p";
} {
  const conservativeLadder =
    isMobile || isInstagram || isArc || isSafariOrIOSWebKit;

  let preferPlayback: "native" | "mse" | undefined;
  if (isInstagram || isSafariOrIOSWebKit) preferPlayback = "native";
  else if (isArc) preferPlayback = "mse";

  const capRenditionToPlayerSize =
    isInstagram || isMobile || isArc || isSafariOrIOSWebKit
      ? (true as const)
      : undefined;

  return {
    maxResolution: conservativeLadder ? "720p" : "1080p",
    ...(preferPlayback ? { preferPlayback } : {}),
    ...(capRenditionToPlayerSize ? { capRenditionToPlayerSize } : {}),
    ...(isInstagram || isMobile || isSafariOrIOSWebKit
      ? { maxAutoResolution: "720p" as const }
      : {}),
  };
}

/**
 * Soft resume reasons — always allowed after first play (even when conversionReel=false).
 * Aggressive reasons (pause_event, native_pause, watchdog) also run when needsSoftKeepAlive
 * (Chrome mobile / Safari / IG), but never after a finished non-loop teaser.
 */
export function isSoftPlaybackResumeReason(reason: string): boolean {
  if (
    reason === "canplay_after_buffer" ||
    reason === "visibility" ||
    reason === "pageshow" ||
    reason === "window_focus" ||
    reason === "user_tap" ||
    reason === "toggle_play" ||
    reason === "desktop_click"
  ) {
    return true;
  }
  if (reason.endsWith("_recover") || reason.endsWith("_recover_retry")) {
    return true;
  }
  if (reason.endsWith("_muted_fallback")) {
    return true;
  }
  return false;
}
